/* eslint-disable sonarjs/cognitive-complexity */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Formik } from 'formik';
import { PropTypes } from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { Affix, Col, Row } from 'antd';
import { Form, Select, Input } from 'formik-antd';
import { useDispatch } from 'react-redux';
import { Box, FormInput, CustomInput, Button, Text } from 'atoms';
import { validateMobileNumber } from '../FormValidation';
import { getPincodeList } from 'store/pageSlice';
import { Debug } from '../Debug';
import { addVendor, updateInfoVendor } from 'store/vendorSlice';
import { gender_list_data, legal_structure_data, vendorUserType } from 'utils/config';
import { ACL, EditModeBtn } from 'components/common';
// import { useQuery } from 'hooks';
import { CancelButton } from 'utils/commonStyled';

const mobileError = 'Must be exactly 10 digits';
const pinError = 'Must be exactly 6 digits';

export default function VendorDetailsForm({ isEditMode = false, formData = {}, otherFormsData }) {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const location = useLocation();
	// const [query] = useQuery();
	const formRef = useRef();
	const [editMode, setEditMode] = useState(false);
	const [isCreate, setIsCreate] = useState(false);

	const FormSchema = useMemo(
		() =>
			Yup.object({
				vendor_type: Yup.string().required().label('Vendor Type'),
				vendor_name: Yup.string()
					.required()
					.min(3)
					.max(63)
					.label('Vendor Name')
					.matches(
						/^([a-zA-Z0-9]+\s)*[a-zA-Z0-9]+$/,
						'Vendor name should be alpha-numeric'
					),
				short_name: Yup.string()
					.required()
					.min(3)
					.max(16)
					.label('Short Name')
					.matches(
						/^([a-zA-Z0-9]+\s)*[a-zA-Z0-9]+$/,
						'Short name should be alpha-numeric'
					),
				cp_first_name: Yup.string()
					.min(1)
					.max(32)
					.label('Contact Person First Name')
					.matches(
						/^[a-zA-Z]*$/,
						'Contact Person First Name should only contain alphabets'
					),
				cp_middle_name: Yup.string()
					.min(1)
					.max(32)
					.label('Contact Person Middle Name')
					.matches(
						/^[a-zA-Z]*$/,
						'Contact Person Middle Name should only contain alphabets'
					),
				cp_last_name: Yup.string()
					.min(1)
					.max(32)
					.label('Contact Person last Name')
					.matches(
						/^[a-zA-Z]*$/,
						'Contact Person last Name should only contain alphabets'
					),
				cp_gender: Yup.string()
					.label('Gender')
					.matches(/^[a-zA-Z]*$/, 'Please enter valid Gender'),
				email: Yup.string()
					.email('Must be a valid email')
					.label('Email')
					.when('vendor_type', (vendor_type, schema) => {
						if (vendor_type !== 'temporary') return schema.required();
						return schema;
					}),
				password: Yup.string()
					.label('Password')
					.min(6)
					.max(16)
					.when('vendor_type', (vendor_type, schema) => {
						if (isEditMode || vendor_type === 'temporary') return schema.notRequired();
						return schema.required();
					}),
				address: Yup.string().min(6).max(200).required().label('Address'),
				legal_structure: Yup.string()
					.label('Legal Structure')
					.when('vendor_type', (vendor_type, schema) => {
						if (vendor_type !== 'temporary') return schema.required();
						return schema;
					}),
				mobile_number: Yup.string()
					.required()
					.label('Mobile Number')
					.min(10, mobileError)
					.max(10, mobileError)
					.test('Not a valid Mobile Number', (value) => validateMobileNumber(value)),
				alternate_number: Yup.string()
					.nullable()
					.label('Alternative Mobile Number')
					.min(10, mobileError)
					.max(10, mobileError)
					.test('Not a valid Alternative Mobile Number', (value) =>
						validateMobileNumber(value)
					),
				pin_code: Yup.string()
					.required()
					.label('Pin Code')
					.min(6, pinError)
					.max(6, pinError)
					.matches(/^[0-9]*$/, 'Please enter valid pin code'),
				zone: Yup.string().label('Zone').required(),
				state: Yup.string().label('State').required(),
				city: Yup.string().label('City').required(),
			}),
		[isEditMode]
	);

	const initialValues = useMemo(
		() => ({
			vendor_type: formData?.vendor_type || undefined,
			vendor_name: formData?.vendor_info?.vendor_name || undefined,
			short_name: formData?.vendor_info?.short_name || undefined,
			cp_first_name: formData?.vendor_info?.cp_first_name || undefined,
			cp_middle_name: formData?.vendor_info?.cp_middle_name || undefined,
			cp_last_name: formData?.vendor_info?.cp_last_name || undefined,
			cp_gender: formData?.vendor_info?.cp_gender || undefined,
			legal_structure: formData?.vendor_info?.legal_structure || undefined,
			email: formData?.vendor_info?.email || undefined,
			mobile_number: formData?.vendor_info?.mobile_number || undefined,
			alternate_number: formData?.vendor_info?.alternate_number || undefined,
			address: formData?.vendor_info?.address || undefined,
			pin_code: formData?.vendor_info?.pin_code || undefined,
			zone: formData?.vendor_info?.zone || undefined,
			state: formData?.vendor_info?.state || undefined,
			city: formData?.vendor_info?.city || undefined,
			password: undefined,
			...(isEditMode && { id: formData?.id }),
		}),
		[formData, isEditMode]
	);

	const handleSubmit = (values, { setErrors, setSubmitting }) => {
		console.log(otherFormsData);
		const {
			vendor_type,
			vendor_name,
			short_name,
			cp_first_name,
			cp_middle_name,
			cp_last_name,
			cp_gender,
			legal_structure,
			email,
			mobile_number,
			alternate_number,
			address,
			pin_code,
			zone,
			state,
			city,
			password,
		} = values;

		let payload = {};

		console.log(setErrors, setSubmitting);

		if (isEditMode) {
			payload = {
				vendor_info: {
					vendor_type,
					vendor_name,
					short_name,
					cp_first_name,
					cp_middle_name,
					cp_last_name,
					cp_gender,
					legal_structure,
					email,
					mobile_number,
					alternate_number,
					address,
					pin_code,
					zone,
					state,
					city,
				},
				ids: otherFormsData,
			};
			payload.update_type = 'vendor';
			payload.vendor_type = values?.vendor_type;

			console.log(payload);
			dispatch(
				updateInfoVendor({
					req_data: payload,
					setErrors,
					isEditMode,
				})
			)
				.then((res) => {
					console.log(res);
					window.location = `${window.origin}/vendor/edit/${res.data.id}?tab=2`;

					setSubmitting(false);
				})
				.catch(() => {
					setSubmitting(false);
				});
		} else {
			payload.create_type = 'vendor';
			payload.password = password;
			payload.vendor_info = values;
			payload.vendor_type = values?.vendor_type;

			console.log(payload);

			dispatch(addVendor({ req_data: payload, setErrors, isEditMode }))
				.then((res) => {
					console.log(res);
					window.location = `${window.origin}/vendor/edit/${res.data.id}?tab=2`;

					setSubmitting(false);
				})
				.catch(() => {
					setSubmitting(false);
				});
		}
	};

	function handlePicodeChange(e) {
		console.log('handlePicodeChange', e?.target?.value);

		const pin_code = e?.target?.value;

		if (pin_code?.length === 6) {
			dispatch(getPincodeList(pin_code))
				.then((res) => {
					console.log('pincode response', res?.rows[0]);
					const { city, state, zone } = res?.rows?.[0] || {};
					if (formRef?.current) {
						formRef.current?.setFieldValue('city', city);
						formRef.current?.setFieldValue('state', state);
						formRef.current?.setFieldValue('zone', zone);
					}
				})
				.catch((er) => {
					console.log('pincode error', er);
				});
		} else {
			console.log('not a valid pincode');
			if (formRef?.current) {
				formRef.current?.setFieldValue('city', undefined);
				formRef.current?.setFieldValue('state', undefined);
				formRef.current?.setFieldValue('zone', undefined);
			}
		}
	}

	function handleVendorType(e, setFieldValue, setFieldError) {
		if (e === 'temporary') {
			setFieldValue('email', 'default@ripple.com');
			setFieldValue('password', '123456');
			setFieldValue('legal_structure', 'LLP');
		}
		if (e === 'regular') {
			setFieldValue('email', undefined);
			setFieldValue('password', undefined);
			setFieldValue('legal_structure', undefined);
			setFieldError('email');
			setFieldError('password');
			setFieldError('legal_structure');
		}
	}
	function handleEdit() {
		console.log('before edit mode ', editMode);
		setEditMode(!editMode);
		console.log('after mode ', editMode);
	}
	useEffect(() => {
		setIsCreate(location.pathname.split('/').pop() === 'addSection');
	});
	return (
		<Formik
			initialValues={initialValues}
			validationSchema={FormSchema}
			onSubmit={handleSubmit}
			enableReinitialize
			innerRef={formRef}
			initialTouched={{
				zone: true,
				state: true,
				city: true,
			}}
		>
			{({ values, errors, setFieldValue, setFieldError }) => (
				<>
					<ACL action="update" module="vendor">
						{isEditMode && (
							<Row gutter={16} justify="space-between">
								<div></div>
								<EditModeBtn handleEdit={handleEdit} />
							</Row>
						)}
					</ACL>
					<Form className={editMode === false && !isCreate ? 'disabled-div' : ''}>
						{otherFormsData?.vendor_type === 'temporary' && (
							<Affix offsetTop={10}>
								<Box
									background="red"
									padding="5px"
									color="#fff"
									fontSize="20px"
									marginTop="6px"
								>
									{' '}
									Note: Please fill All section data if its temporary Vendor{' '}
								</Box>
							</Affix>
						)}
						<Box padding="1.5rem 2rem" background="#fff">
							<Row gutter={16} justify="space-between">
								<Col lg={12}>
									<FormInput name="vendor_type" label="Vendor Type*">
										<Select
											name="vendor_type"
											disabled={isEditMode}
											filterOption={(input, option) =>
												option.label
													.toLowerCase()
													.indexOf(input.toLowerCase()) >= 0
											}
											options={vendorUserType}
											placeholder="Select"
											onChange={(e) =>
												handleVendorType(e, setFieldValue, setFieldError)
											}
										/>
									</FormInput>
								</Col>
								<Col lg={12}>
									{values.vendor_type !== 'temporary' && (
										<FormInput name="legal_structure" label="Legal Structure *">
											<Select
												name="legal_structure"
												filterOption={(input, option) =>
													option.label
														.toLowerCase()
														.indexOf(input.toLowerCase()) >= 0
												}
												placeholder="Select"
												options={legal_structure_data}
											/>
										</FormInput>
									)}
								</Col>
							</Row>

							<Row gutter={16} justify="space-between">
								<Col lg={12}>
									<FormInput name="vendor_name" label="Vendor Name *">
										<CustomInput name="vendor_name" />
									</FormInput>
								</Col>
								<Col lg={12}>
									<FormInput name="short_name" label="Short Name *">
										<CustomInput name="short_name" />
									</FormInput>
								</Col>
							</Row>

							<Row gutter={16} justify="space-between">
								<Col lg={12}>
									<FormInput
										name="cp_first_name"
										label="Contact Person First Name"
									>
										<CustomInput name="cp_first_name" />
									</FormInput>
								</Col>

								<Col lg={12}>
									<FormInput
										name="cp_middle_name"
										label="Contact Person Middle Name"
									>
										<CustomInput name="cp_middle_name" />
									</FormInput>
								</Col>
							</Row>

							<Row gutter={16} justify="space-between">
								<Col lg={12}>
									<FormInput name="cp_last_name" label="Contact Person Last Name">
										<CustomInput name="cp_last_name" />
									</FormInput>
								</Col>

								<Col lg={12}>
									<FormInput name="cp_gender" label="Gender *">
										<Select
											name="cp_gender"
											filterOption={(input, option) =>
												option.label
													.toLowerCase()
													.indexOf(input.toLowerCase()) >= 0
											}
											placeholder="Select"
											options={gender_list_data}
										/>
									</FormInput>
								</Col>
							</Row>

							<Row gutter={16} justify="space-between">
								<Col lg={12}>
									{values.vendor_type !== 'temporary' && (
										<FormInput name="email" label="Email Address *">
											<CustomInput
												name="email"
												autoComplete="new-password"
												role="presentation"
											/>
										</FormInput>
									)}
								</Col>
								<Col lg={12}>
									{values.vendor_type !== 'temporary' && !isEditMode && (
										<FormInput name="password" label="Password *">
											<Input.Password
												name="password"
												autoComplete="new-password"
												role="presentation"
											/>
										</FormInput>
									)}
								</Col>
							</Row>

							<Row gutter={16} justify="space-between">
								<Col lg={12}>
									<FormInput name="mobile_number" label="Mobile Number *">
										<CustomInput name="mobile_number" maxLength={10} />
									</FormInput>
								</Col>
								<Col lg={12}>
									<FormInput
										name="alternate_number"
										label="Alternate Mobile Number"
									>
										<CustomInput name="alternate_number" maxLength={10} />
									</FormInput>
								</Col>
							</Row>

							<Row gutter={16} justify="space-between">
								<Col lg={12}>
									{' '}
									<FormInput name="address" label="Address *">
										<CustomInput name="address" />
									</FormInput>
								</Col>
								<Col lg={12}>
									<FormInput
										name="pin_code"
										label="Pincode *"
										onChange={handlePicodeChange}
									>
										<CustomInput
											name="pin_code"
											disabled={isEditMode}
											maxLength={6}
										/>
									</FormInput>
								</Col>
							</Row>

							<Row gutter={16} justify="space-between">
								<Col lg={12}>
									<FormInput name="zone" label="Zone *">
										<CustomInput name="zone" disabled />
									</FormInput>
								</Col>
								<Col lg={12}>
									<FormInput name="state" label="State *">
										<CustomInput name="state" disabled />
									</FormInput>
								</Col>
							</Row>

							<Row gutter={16} justify="space-between">
								<Col lg={12}>
									<FormInput name="city" label="City *">
										<CustomInput name="city" disabled />
									</FormInput>
								</Col>
								<Col lg={12}></Col>
							</Row>
							<Row gutter={16} justify="space-between">
								<Col lg={12}>
									{errors?.zone && (
										<Text mt="2.5rem" color="red">
											Please enter valid pincode. Zone, State, City are
											Required *
										</Text>
									)}
								</Col>
							</Row>
							<Row gutter={16} justify="space-between">
								{values.vendor_type !== 'temporary' && errors?.email && (
									<Text mt="2.5rem" color="red">
										Email is a required field
									</Text>
								)}
								{values.vendor_type !== 'temporary' &&
									!isEditMode &&
									errors?.password && (
										<Text mt="2.5rem" color="red">
											Password is a required field
										</Text>
									)}
								{values.vendor_type !== 'temporary' && errors?.legal_structure && (
									<Text mt="2.5rem" color="red">
										Legal Structure is a required field
									</Text>
								)}
							</Row>
						</Box>

						<Box backgroundColor="#f5f5f5" textAlign="right" padding="1.5rem">
							<CancelButton onClick={() => navigate('/vendor-list')} type="button">
								Cancel
							</CancelButton>
							<Button borderRadius="4px" backgroundColor="#18405d" type="submit">
								{isEditMode ? 'Update' : 'Submit'}
							</Button>
						</Box>
						<Debug />
					</Form>
				</>
			)}
		</Formik>
	);
}

VendorDetailsForm.propTypes = {
	isEditMode: PropTypes.bool,
	formData: PropTypes.object,
	otherFormsData: PropTypes.object,
};
